import type { KartState, RaceState } from "../game/simulation/types";
import type { KartSnapshot, RaceSnapshot } from "./protocol";
import { normalizeAvatarLogin, SERVER_TICK_RATE } from "./protocol";
import { cloneCup } from "../game/championship";
import { createCourseEventState, getCourseEventDefinition, getCourseEventDefinitionById } from "../game/simulation/courseEvents";
import { deriveRaceVariant } from "../game/simulation/raceVariants";
import { getCustomTrackLayout } from "../game/simulation/track";
import { isCustomTrackIdCandidate } from "./customTrackDistribution";

interface SnapshotFrame {
  tick: number;
  receivedAt: number;
  state: RaceSnapshot;
}

const INTERPOLATION_TICKS = 6;

function finiteCooldown(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function finiteCount(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function inflateKart(kart: KartSnapshot): KartState {
  const inflated: KartState = {
    ...kart,
    controller: kart.kind,
    autopilot: kart.kind === "human" && kart.autopilot === true,
    classicJumpedSampleIndex: typeof kart.classicJumpedSampleIndex === "number" && Number.isFinite(kart.classicJumpedSampleIndex)
      ? Math.floor(kart.classicJumpedSampleIndex) : null,
    twitchLogin: null,
    twitchDirective: "steady",
    directiveUntil: 0,
    itemCooldown: 0,
    itemHeldAt: 0,
    lastUseSeq: 0,
    itemRolls: 0,
    itemDodgeTime: finiteCooldown(kart.itemDodgeTime),
    itemImmunityTime: finiteCooldown(kart.itemImmunityTime),
    ghostTime: finiteCooldown(kart.ghostTime),
    hiccupTime: finiteCooldown(kart.hiccupTime),
    spinRecoveryTime: finiteCooldown(kart.spinRecoveryTime),
    itemHitCount: finiteCount(kart.itemHitCount),
    lastItemHit: kart.lastItemHit ?? null,
    itemStealCount: finiteCount(kart.itemStealCount),
    lastItemStolen: kart.lastItemStolen ?? null,
    itemGuardCount: finiteCount(kart.itemGuardCount),
    kegDodgeCount: finiteCount(kart.kegDodgeCount),
    lastItemDodge: kart.lastItemDodge ?? null,
    pendingItemUse: false,
    laneBias: 0,
    laneBiasUntil: 0,
    rampCooldown: 0,
    hazardCooldown: 0,
    courseEventCooldown: finiteCooldown(kart.courseEventCooldown),
    courseEventHitCount: finiteCount(kart.courseEventHitCount),
  };
  const avatarLogin = inflated.controller === "ai" ? undefined : normalizeAvatarLogin(kart.avatarLogin);
  if (avatarLogin) inflated.avatarLogin = avatarLogin;
  else delete inflated.avatarLogin;
  return inflated;
}

function inflateCourseEvent(snapshot: RaceSnapshot): RaceState["courseEvent"] {
  const raceVariant = snapshot.raceVariant ? { ...snapshot.raceVariant } : deriveRaceVariant(0, snapshot.trackId);
  const fallbackCourseEvent = createCourseEventState(snapshot.trackId, raceVariant);
  const knownCourseEvent = snapshot.courseEvent
    ? getCourseEventDefinitionById(snapshot.courseEvent.definitionId || snapshot.courseEvent.id)
    : undefined;
  return snapshot.courseEvent && fallbackCourseEvent ? {
    ...fallbackCourseEvent,
    ...snapshot.courseEvent,
    patternIndex: knownCourseEvent?.patternIndex ?? snapshot.courseEvent.patternIndex ?? fallbackCourseEvent.patternIndex,
  } : fallbackCourseEvent;
}

export function inflateRaceSnapshot(snapshot: RaceSnapshot): RaceState {
  const raceVariant = snapshot.raceVariant ? { ...snapshot.raceVariant } : deriveRaceVariant(0, snapshot.trackId);
  const courseEvent = inflateCourseEvent(snapshot);
  return {
    trackId: snapshot.trackId,
    raceVariant,
    raceClass: snapshot.raceClass,
    phase: snapshot.phase,
    elapsed: snapshot.elapsed,
    countdown: snapshot.countdown,
    totalLaps: snapshot.totalLaps,
    trackLength: snapshot.trackLength,
    karts: snapshot.karts.map(inflateKart),
    itemBoxes: snapshot.itemBoxes.map((box) => ({ ...box })),
    projectiles: snapshot.projectiles.map((projectile) => ({ ...projectile })),
    hazards: snapshot.hazards.map((hazard) => ({ ...hazard })),
    courseEvent,
    nextProjectileId: 1,
    cup: snapshot.cup ? cloneCup(snapshot.cup) : undefined,
  };
}

function copyKart(target: KartState, source: KartSnapshot | KartState): void {
  const controller = "kind" in source ? source.kind : source.controller;
  target.id = source.id;
  target.driverProfileId = source.driverProfileId;
  const avatarLogin = normalizeAvatarLogin(source.avatarLogin);
  if (avatarLogin && controller !== "ai") target.avatarLogin = avatarLogin;
  else delete target.avatarLogin;
  target.name = source.name;
  target.color = source.color;
  target.accentColor = source.accentColor;
  target.variant = source.variant;
  target.livery = source.livery;
  target.wheelStyle = source.wheelStyle;
  target.driverColor = source.driverColor;
  target.weightClass = source.weightClass;
  target.pattern = source.pattern;
  target.topper = source.topper;
  target.sticker = source.sticker;
  target.underglow = source.underglow;
  target.horn = source.horn;
  target.skidColor = source.skidColor;
  target.trail = source.trail;
  target.raceNumber = source.raceNumber;
  target.controller = controller;
  target.autopilot = controller === "human" && source.autopilot === true;
  target.x = source.x;
  target.y = source.y;
  target.z = source.z;
  target.velocityX = source.velocityX;
  target.velocityZ = source.velocityZ;
  target.yaw = source.yaw;
  target.speed = source.speed;
  target.launchCharge = source.launchCharge;
  target.distance = source.distance;
  target.lateral = source.lateral;
  target.driftCharge = source.driftCharge;
  target.driftDirection = source.driftDirection;
  target.boostTime = source.boostTime;
  target.itemDodgeTime = finiteCooldown(source.itemDodgeTime);
  target.itemImmunityTime = finiteCooldown(source.itemImmunityTime);
  target.ghostTime = finiteCooldown(source.ghostTime);
  target.hiccupTime = finiteCooldown(source.hiccupTime);
  target.draftCharge = source.draftCharge;
  target.draftBoostTime = source.draftBoostTime;
  target.draftCooldown = source.draftCooldown;
  target.draftTargetId = source.draftTargetId;
  target.draftBoostCount = source.draftBoostCount;
  target.heldItem = source.heldItem;
  target.itemHitCount = finiteCount(source.itemHitCount);
  target.lastItemHit = source.lastItemHit ?? null;
  target.itemStealCount = finiteCount(source.itemStealCount);
  target.lastItemStolen = source.lastItemStolen ?? null;
  target.itemGuardCount = finiteCount(source.itemGuardCount);
  target.kegDodgeCount = finiteCount(source.kegDodgeCount);
  target.lastItemDodge = source.lastItemDodge ?? null;
  target.shieldTime = source.shieldTime;
  target.spinTime = source.spinTime;
  target.spinRecoveryTime = finiteCooldown(source.spinRecoveryTime);
  target.hornSeq = source.hornSeq;
  target.verticalVelocity = source.verticalVelocity;
  target.airborne = source.airborne;
  target.airTime = source.airTime;
  target.trickQueued = source.trickQueued;
  target.classicJumpedSampleIndex = typeof source.classicJumpedSampleIndex === "number" && Number.isFinite(source.classicJumpedSampleIndex)
    ? Math.floor(source.classicJumpedSampleIndex) : null;
  target.classicSampleIndex = source.classicSampleIndex;
  target.classicNextCheckpoint = source.classicNextCheckpoint;
  target.classicGatesPassed = source.classicGatesPassed;
  target.classicProgress = source.classicProgress;
  target.onShortcut = source.onShortcut;
  target.surface = source.surface;
  target.offRoadTime = source.offRoadTime;
  target.wrongWayTime = source.wrongWayTime;
  target.recoveryTime = source.recoveryTime;
  target.recoveryCount = source.recoveryCount;
  target.courseEventCooldown = finiteCooldown(source.courseEventCooldown);
  target.courseEventHitCount = finiteCount(source.courseEventHitCount);
  target.signatureZoneId = source.signatureZoneId;
  target.signatureZoneTime = source.signatureZoneTime;
  target.signatureZoneTriggerCount = source.signatureZoneTriggerCount;
  target.signatureZoneLastLap = source.signatureZoneLastLap;
  target.safeDistance = source.safeDistance;
  target.lap = source.lap;
  target.place = source.place;
  target.finishedAt = source.finishedAt;
}

function lerpAngle(from: number, to: number, alpha: number): number {
  const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + delta * alpha;
}

export class RemoteInterpolator {
  private readonly frames: SnapshotFrame[] = [];
  private cache: RaceState | null = null;

  reset(): void {
    this.frames.length = 0;
    this.cache = null;
  }

  push(tick: number, state: RaceSnapshot, receivedAt = performance.now()): boolean {
    // A custom runtime id is not renderable until its descriptor has been
    // registered. Keep the old interpolation buffer intact so callers can
    // queue the snapshot and retry after the definition arrives.
    if (isCustomTrackIdCandidate(state.trackId)) {
      try {
        if (getCustomTrackLayout(state.trackId) === null) return false;
      } catch {
        return false;
      }
    }
    let latest: SnapshotFrame | undefined = this.frames[this.frames.length - 1];
    const previousSeed = latest?.state.raceVariant?.seed ?? 0;
    const nextSeed = state.raceVariant?.seed ?? 0;
    const previousRoute = latest?.state.cup?.routeId ?? "brewers-tour";
    const nextRoute = state.cup?.routeId ?? "brewers-tour";
    if (latest && (latest.state.trackId !== state.trackId || previousSeed !== nextSeed || previousRoute !== nextRoute)) {
      this.reset();
      latest = undefined;
    }
    if (latest && tick <= latest.tick) return true;
    let replacementCache: RaceState | null = null;
    if (!this.cache || this.cache.karts.length !== state.karts.length || this.cache.karts.some((kart, index) => kart.id !== state.karts[index]?.id)) {
      try {
        replacementCache = inflateRaceSnapshot(state);
      } catch {
        return false;
      }
    }
    this.frames.push({ tick, state, receivedAt });
    if (this.frames.length > 12) this.frames.splice(0, this.frames.length - 12);
    if (replacementCache) this.cache = replacementCache;
    return true;
  }

  sample(now: number, predicted: RaceState, localKartId: string | null): RaceState {
    const latest = this.frames[this.frames.length - 1];
    if (!latest || !this.cache) return predicted;
    const targetTick = latest.tick - INTERPOLATION_TICKS + Math.max(0, now - latest.receivedAt) * SERVER_TICK_RATE / 1000;
    let older = this.frames[0];
    let newer = latest;
    for (const frame of this.frames) {
      if (frame.tick <= targetTick) older = frame;
      if (frame.tick >= targetTick) {
        newer = frame;
        break;
      }
    }
    const span = Math.max(1, newer.tick - older.tick);
    const alpha = Math.max(0, Math.min(1, (targetTick - older.tick) / span));
    const cache = this.cache;
    cache.phase = latest.state.phase;
    cache.trackId = latest.state.trackId;
    cache.raceVariant = latest.state.raceVariant ? { ...latest.state.raceVariant } : deriveRaceVariant(0, latest.state.trackId);
    cache.raceClass = latest.state.raceClass;
    cache.elapsed = latest.state.elapsed;
    cache.countdown = latest.state.countdown;
    cache.totalLaps = latest.state.totalLaps;
    cache.trackLength = latest.state.trackLength;
    cache.itemBoxes = latest.state.itemBoxes;
    cache.projectiles = latest.state.projectiles;
    cache.hazards = latest.state.hazards;
    const latestEvent = latest.state.courseEvent;
    const eventDefinition = getCourseEventDefinition(latest.state.trackId, cache.raceVariant);
    if (latestEvent && eventDefinition) {
      const patternIndex = getCourseEventDefinitionById(latestEvent.definitionId || latestEvent.id)?.patternIndex
        ?? latestEvent.patternIndex ?? eventDefinition.patternIndex;
      if (cache.courseEvent) Object.assign(cache.courseEvent, latestEvent, { patternIndex });
      else cache.courseEvent = { ...latestEvent, patternIndex };
      const olderEvent = older.state.courseEvent;
      const newerEvent = newer.state.courseEvent;
      if (olderEvent && newerEvent && olderEvent.id === newerEvent.id && olderEvent.lap === newerEvent.lap && olderEvent.phase === newerEvent.phase) {
        cache.courseEvent.phaseTime = olderEvent.phaseTime + (newerEvent.phaseTime - olderEvent.phaseTime) * alpha;
        cache.courseEvent.phaseRemaining = olderEvent.phaseRemaining + (newerEvent.phaseRemaining - olderEvent.phaseRemaining) * alpha;
      }
    } else {
      cache.courseEvent = eventDefinition ? createCourseEventState(latest.state.trackId, cache.raceVariant) : null;
    }
    cache.cup = latest.state.cup ? cloneCup(latest.state.cup) : undefined;

    const predictedLocal = localKartId ? predicted.karts.find((kart) => kart.id === localKartId) : null;
    for (let index = 0; index < latest.state.karts.length; index += 1) {
      const latestKart = latest.state.karts[index];
      const target = cache.karts[index];
      if (predictedLocal && latestKart.id === localKartId) {
        copyKart(target, predictedLocal);
        // Personality identity is server-owned, even for the locally
        // predicted kart; never let a stale local copy survive a slot reuse.
        target.driverProfileId = latestKart.driverProfileId;
        target.autopilot = latestKart.kind === "human" && latestKart.autopilot === true;
        const avatarLogin = latestKart.kind === "ai" ? undefined : normalizeAvatarLogin(latestKart.avatarLogin);
        if (avatarLogin) target.avatarLogin = avatarLogin;
        else delete target.avatarLogin;
        continue;
      }
      copyKart(target, latestKart);
      const a = older.state.karts.find((kart) => kart.id === latestKart.id);
      const b = newer.state.karts.find((kart) => kart.id === latestKart.id);
      if (!a || !b) continue;
      target.x = a.x + (b.x - a.x) * alpha;
      target.y = a.y + (b.y - a.y) * alpha;
      target.z = a.z + (b.z - a.z) * alpha;
      target.velocityX = a.velocityX + (b.velocityX - a.velocityX) * alpha;
      target.velocityZ = a.velocityZ + (b.velocityZ - a.velocityZ) * alpha;
      target.yaw = lerpAngle(a.yaw, b.yaw, alpha);
      target.speed = a.speed + (b.speed - a.speed) * alpha;
      target.distance = a.distance + (b.distance - a.distance) * alpha;
      target.lateral = a.lateral + (b.lateral - a.lateral) * alpha;
      target.driftCharge = a.driftCharge + (b.driftCharge - a.driftCharge) * alpha;
      target.draftCharge = a.draftCharge + (b.draftCharge - a.draftCharge) * alpha;
      target.draftBoostTime = a.draftBoostTime + (b.draftBoostTime - a.draftBoostTime) * alpha;
      target.draftCooldown = a.draftCooldown + (b.draftCooldown - a.draftCooldown) * alpha;
      target.offRoadTime = a.offRoadTime + (b.offRoadTime - a.offRoadTime) * alpha;
      target.wrongWayTime = a.wrongWayTime + (b.wrongWayTime - a.wrongWayTime) * alpha;
      target.recoveryTime = a.recoveryTime + (b.recoveryTime - a.recoveryTime) * alpha;
      const olderGhost = finiteCooldown(a.ghostTime);
      const newerGhost = finiteCooldown(b.ghostTime);
      target.ghostTime = olderGhost + (newerGhost - olderGhost) * alpha;
      const olderCooldown = finiteCooldown(a.courseEventCooldown);
      const newerCooldown = finiteCooldown(b.courseEventCooldown);
      target.courseEventCooldown = olderCooldown + (newerCooldown - olderCooldown) * alpha;
    }
    return cache;
  }
}
